// Module: exchangeHours.js
// Purpose: MDMS-fed exchange open/closed store for Indian market venues, used
//          by the live price ticker badge and the #44 delivery-stall watchdog.
//          Replaces the old static IST-hours map (#31/exchange-session-ssot
//          WP-6): MDMS is now the single calendar/holiday authority, reached
//          via GET /exchange-status/all at boot and the `exchange-status.update`
//          WS topic thereafter (fed into this module by WebSocketProvider, the
//          topic's sole subscriber — see WebSocketProvider.jsx).

import { services } from '../services/api';

/**
 * Tri-state sentinel returned when this module has no MDMS-sourced answer for
 * an exchange (never asked yet, MDMS unreachable, or an exchange code MDMS
 * doesn't carry). DK-4: there is deliberately NO fallback to a static hours
 * table here — an unanswered question stays an unanswered question.
 *
 * //! Do not coerce a tri-state return with `!x`. `EXCHANGE_STATUS_UNKNOWN` is a
 * //! non-empty string, so `!x` is `false` for both `true` and `EXCHANGE_STATUS_UNKNOWN`
 * //! — which is exactly the fail-open behaviour this module has always given
 * //! callers that treat "not known-closed" as "don't show CLOSED". A caller that
 * //! must distinguish the UNKNOWN case (e.g. render a distinct grey badge, or
 * //! decide the ticker's 'closed' state) MUST compare `=== false` /
 * //! `=== EXCHANGE_STATUS_UNKNOWN` explicitly rather than relying on truthiness.
 */
export const EXCHANGE_STATUS_UNKNOWN = 'UNKNOWN';

//--> The Indian venues the #44 stall watchdog's union predicate asks about.
//*   Distinct from DK-3's foreign-venue list (backend-side, exchange_hours.py) —
//*   this is only the venues isAnyIndianVenueOpenNow aggregates over.
const INDIAN_VENUES = ['NSE', 'BSE', 'MCX', 'NFO', 'BFO'];

//--> The MDMS-fed store. Keyed by UPPERCASED exchange code:
//*   { [code]: { isOpen: boolean, hoursVerified: boolean, updatedAt: number } }
//!   Sole writers: loadExchangeStatusFromApi() (REST, once at boot) and
//!   updateExchangeStatus() (WS push, called by WebSocketProvider for every
//!   `exchange-status.update` frame it receives). Nothing else may write here.
const store = new Map();

//--> Dedup concurrent initial-load callers (every mounted consumer of this
//*   module could otherwise fire its own GET /exchange-status/all).
let initialLoadPromise = null;

const normalizeCode = (exchange) => (exchange ? String(exchange).trim().toUpperCase() : null);

const applyStatusRow = (code, row) => {
    if (!code || !row) return;
    store.set(code, {
        isOpen: Boolean(row.is_open),
        //--> R2/DK-3: absent key (pre-deploy record, ≤1 writer cadence) reads
        //*   verified=true, matching the dashboard's `?? true` default (C-12).
        hoursVerified: row.hours_verified ?? true,
        updatedAt: Date.now(),
    });
};

/**
 * One-shot REST load of every venue's current status from MDMS via the
 * gateway (`services.exchangeStatus.getAllStatus` -> GET /exchange-status/all,
 * `api.js`). Idempotent: concurrent callers share one in-flight request, so
 * every consumer that mounts before the WS push arrives can call this safely.
 * Call once from WebSocketProvider at app start.
 */
export const loadExchangeStatusFromApi = () => {
    if (initialLoadPromise) return initialLoadPromise;
    initialLoadPromise = services.exchangeStatus.getAllStatus()
        .then((response) => {
            const exchanges = response?.data?.exchanges;
            if (!exchanges || typeof exchanges !== 'object') return;
            Object.entries(exchanges).forEach(([code, exchange]) => {
                const row = typeof exchange === 'string' ? JSON.parse(exchange) : exchange;
                applyStatusRow(normalizeCode(row?.exchange || code), row);
            });
        })
        .catch((err) => {
            //! DK-4: MDMS unreachable at boot -> store stays empty -> every
            //! predicate below answers EXCHANGE_STATUS_UNKNOWN. No silent
            //! fallback to a guessed value.
            console.warn('[DIAG] exchangeHours: GET /exchange-status/all failed', err?.message);
        });
    return initialLoadPromise;
};

/**
 * Live feed for the `exchange-status.update` WS topic. WebSocketProvider is
 * the topic's ONE subscriber (per-resource fan-out via context) and calls this
 * for every payload it receives, keeping this module's store current without
 * a second subscription anywhere else in the app.
 */
export const updateExchangeStatus = (exchange, payload) => {
    const code = normalizeCode(exchange || payload?.exchange);
    applyStatusRow(code, payload);
};

const readStatus = (exchange) => {
    const code = normalizeCode(exchange);
    return code ? store.get(code) || null : null;
};

/**
 * Is the given exchange open at this instant, per MDMS?
 * Tri-state: `true` / `false` / `EXCHANGE_STATUS_UNKNOWN` when this module has
 * no MDMS-sourced entry for the exchange (unknown code, or the store hasn't
 * been populated yet — see EXCHANGE_STATUS_UNKNOWN's docstring for how callers
 * should treat that third state).
 */
export const isExchangeOpenNow = (exchange) => {
    const entry = readStatus(exchange);
    if (!entry) return EXCHANGE_STATUS_UNKNOWN;
    return entry.isOpen;
};

/**
 * Is ANY Indian venue we trade currently open, per MDMS? (union over
 * INDIAN_VENUES — effectively NSE/BSE/NFO/BFO session widened by MCX's
 * commodity hours, same union semantics as before V12b F2, now calendar- and
 * holiday-aware because the answer comes from MDMS instead of static hours.)
 *
 * //! V12b F2: this gates the #44 delivery-stall watchdog. Tick silence is only
 * //! evidence of a FAULT while some venue is actually trading; outside these
 * //! hours silence is the correct, expected state.
 *
 * //* Deliberately a UNION, not per-instrument — see isTickVenueOpenNow for the
 * //* per-venue predicate the watchdog actually asks per tick source.
 *
 * Tri-state: EXCHANGE_STATUS_UNKNOWN when none of INDIAN_VENUES has an MDMS
 * entry yet (store not populated) — never a guessed `true`.
 */
export const isAnyIndianVenueOpenNow = () => {
    let sawKnownVenue = false;
    let anyOpen = false;
    INDIAN_VENUES.forEach((code) => {
        const entry = store.get(code);
        if (!entry) return;
        sawKnownVenue = true;
        if (entry.isOpen) anyOpen = true;
    });
    if (!sawKnownVenue) return EXCHANGE_STATUS_UNKNOWN;
    return anyOpen;
};

/**
 * Is the venue that feeds THIS instrument trading right now, per MDMS?
 *
 * //! V12B-11: the per-venue resolver the #44 delivery-stall predicate asks INSTEAD of
 * //! the union above, so a watchlist holding only NSE names doesn't read its own
 * //! correct post-close silence as a delivery fault while MCX (widest window) is
 * //! still open.
 *
 * //! Falls back to the UNION (isAnyIndianVenueOpenNow), never to a bare `true`,
 * //! when `exchange` is blank or MDMS doesn't carry the code — an exchange this
 * //! store does not recognise degrades to "conservatively assume some venue we
 * //! trade could be open", exactly as before.
 */
export const isTickVenueOpenNow = (exchange) => {
    const entry = readStatus(exchange);
    if (!entry) return isAnyIndianVenueOpenNow();
    return entry.isOpen;
};

/**
 * "YYYY-MM-DD" for the current IST calendar day.
 *
 * //! V12b: used to stamp which TRADING DAY a delivery baseline belongs to. No
 * //! Indian venue crosses midnight IST (MCX shuts at 23:30), so an IST calendar
 * //! day and a trading day are the same thing here. Pure wall-clock formatting —
 * //! does not touch the MDMS store, so it needs no fallback semantics.
 *
 * //! Why a stamp and not just the watchdog's reset: the reset only runs if a
 * //! watchdog pass happens to land while the session is shut. Suspend the
 * //! laptop at 22:00 and resume at 09:10 and no such pass ever occurs — the
 * //! baseline would still be yesterday's last tick, and the watchdog would fire
 * //! instantly at the open against a stale age. Comparing day-keys closes that
 * //! hole without depending on a timer having run.
 */
export const istTradingDayKey = (when = new Date()) => {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric', month: '2-digit', day: '2-digit',
    });
    //--> en-CA formats as YYYY-MM-DD, which sorts and compares as a plain string.
    return fmt.format(when);
};
